import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import qs from "query-string";
import { useLocation } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import SelectInput from "components/SelectInput";
import { MasterDataContext } from "contexts/MasterDataContext";
import DataNotFound from "components/DataNotFound";
import { FireBaseContext } from "contexts/FireBase";
import BarsGroupChart from "components/BarsGroupChart";
import { Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  IReportTypeOption,
  ReportType,
  initialOptions,
  selectIsPrinting,
  selectIsProgressLoading,
  selectProgressData,
  selectReportType,
  selectedRangeSet,
} from "features/Reports";
import { getRangeList } from "../Utils/Common";
import {
  IProgressDataEntity,
  logAnalyticsEvent,
  transformClassProgressReport,
  transformGradeProgressData,
  transformGradesProgressData,
  transformTeacherProgressData,
  transformTeachersProgressData,
} from "../Utils/ProgressHelpers";
import {
  IProgressClassResponse,
  IProgressGradeResponse,
  IProgressGradesResponse,
  IProgressTeacherResponse,
  IProgressTeachersResponse,
} from "app/services/reports";

const customStyles = {
  heading: {
    display: "flex",
    paddingBottom: "2rem",
    fontWeight: "bold",
    "@media screen and (max-width: 1023px)": {
      fontSize: "12px",
    },
  },
  chartContainer: {
    width: "100%",
    paddingTop: "2rem",
    height: 275,
    padding: "0 20px",
    direction: "ltr",
    "@media screen and (max-width: 1023px)": {
      "& *": {
        fontSize: "10px !important",
      },
    },
  },
  select: {
    width: "225px",
    "@media screen and (max-width: 1023px)": {
      width: "150px",
      "& *": {
        fontSize: "10px !important",
      },
    },
  },
};

export const getBarCategoryGap = (reportType: ReportType) => {
  let barCategoryGap;
  switch (reportType) {
    case ReportType.Teacher_Report:
    case ReportType.Grade_Report:
      barCategoryGap = "30%";
      break;
    case ReportType.Grades_Report:
      barCategoryGap = "40%";
      break;
    default:
      barCategoryGap = "20%";
      break;
  }
  return barCategoryGap;
};

export const getProgressReport = (
  reportType: ReportType,
  teachersData?: IProgressTeachersResponse,
  teacherData?: IProgressTeacherResponse,
  classData?: IProgressClassResponse,
  gradesDate?: IProgressGradesResponse,
  gradeData?: IProgressGradeResponse
) => {
  try {
    let dataList: IProgressDataEntity[] = [];
    switch (reportType) {
      case ReportType.Teachers_Report:
        if (teachersData?._statusCode === 200 && teachersData?._entity) {
          const progress = [
            ...teachersData?._entity._teacherProgressList.map((item: any) => ({
              teacherCode: item._teacherId,
              assignedLos: item._assignedLos,
              avgCompletedLos: item._completedLos,
              teacherName: item._teacherName,
            })),
          ];
          dataList = transformTeachersProgressData(progress);
        }
        break;
      case ReportType.Teacher_Report:
        if (teacherData?._statusCode === 200 && teacherData?._entity) {
          const progress = [
            ...teacherData?._entity._classProgressList.map((item: any) => ({
              assignedLos: item._assignedLos,
              avgCompletedLos: item._completedLos,
              classCode: item._classId,
              gradeCode: item._gradeCode,
              gradeTitle: item._gradeCode,
              classTitle: item._className,
            })),
          ];
          dataList = transformTeacherProgressData(progress);
        }
        break;
      case ReportType.Grades_Report:
        if (gradesDate?._statusCode === 200 && gradesDate?._entity) {
          const progress = [
            ...gradesDate?._entity._gradeProgressList.map((item: any) => ({
              assignedLos: item._assignedLos,
              avgCompletedLos: item._completedLos,
              gradeCode: item._gradeCode,
            })),
          ];
          dataList = transformGradesProgressData(progress);
        }
        break;
      case ReportType.Grade_Report:
        if (gradeData?._statusCode === 200 && gradeData?._entity) {
          const progress = [
            ...gradeData?._entity._classProgressList.map((item: any) => ({
              assignedLos: item._assignedLos,
              avgCompletedLos: item._completedLos,
              classCode: item._classId,
              classTitle: item._className,
              gradeCode: gradeData?._entity._gradeCode,
              gradeTitle: gradeData?._entity._gradeCode,
            })),
          ];
          dataList = transformGradeProgressData(progress);
        }
        break;
      case ReportType.Class_Report:
        if (classData?._statusCode === 200 && classData?._entity) {
          const progress = [
            ...classData?._entity._studentProgressList.map((item: any) => ({
              assignedLos: item._assignedLos,
              avgCompletedLos: item._completedLos,
              studentName: item._studentName,
              studentCode: item._studentId,
            })),
          ];
          dataList = transformClassProgressReport(progress);
        }
        break;
      default:
        dataList = [];
    }
    return dataList;
  } catch (error) {
    console.log(error);
  }
};

function ProgressSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const [optionsList, setOptionsList] = useState<IReportTypeOption[]>([]);
  let { pathname: url, search } = useLocation();
  const dispatch = useAppDispatch();
  const { logEvent } = useContext(FireBaseContext);
  const progressData = useAppSelector(selectProgressData);
  const reportType = useAppSelector(selectReportType);
  const isProgressLoading = useAppSelector(selectIsProgressLoading);
  const isPrinting = useAppSelector(selectIsPrinting);

  useEffect(() => {
    if (schoolDetails?.academicStartDate) {
      const initOp = initialOptions.map((op) => ({
        ...op,
        label: t(op.label),
      }));
      setOptionsList(
        getRangeList(initOp, schoolDetails.academicStartDate, i18n.dir())
      );
    }
  }, [schoolDetails, i18n.dir()]);

  const clickHandler = (item: {
    activePayload: { payload: { selectedItem: string } }[];
  }) => {
    const queryParam = qs.parse(search);
    let paramName;
    let eventName;
    switch (reportType) {
      case ReportType.Teachers_Report:
        paramName = "teacherCode";
        eventName = "ClickOnSpecificTeacher";
        break;
      case ReportType.Teacher_Report:
      case ReportType.Grade_Report:
        paramName = "classCode";
        eventName = "ClickOnSpecificClass";
        break;
      case ReportType.Grades_Report:
        paramName = "gradeCode";
        eventName = "ClickOnSpecificGrade";
        break;
      default:
        paramName = null;
    }
    if (paramName && item?.activePayload[0].payload.selectedItem) {
      if (eventName) logEvent(eventName, { "Report Type": "Progress" });
      const newQueryParam = {
        [paramName]: item.activePayload[0].payload.selectedItem,
        ...queryParam,
      };
      navigate({ search: qs.stringify(newQueryParam) });
    }
  };

  const getSelectedOption = (event: { currentTarget: IReportTypeOption }) => {
    const {
      currentTarget: { type, value, label },
    } = event;
    logAnalyticsEvent(logEvent, label, reportType, url.substring(1));
    dispatch(selectedRangeSet({ type, value }));
  };
  return (
    <>
      <Typography component="div" sx={customStyles.heading}>
        <span style={{ flexGrow: 1, color: "rgba(60, 180, 229, 1)" }}>
          {t("Progress Reports")}
        </span>
        <Typography component="div" sx={customStyles.select}>
          <SelectInput
            name="range"
            valueList={optionsList}
            defaultValue={initialOptions[0].value}
            getOption={getSelectedOption}
          />
        </Typography>
      </Typography>
      <Typography component="div" sx={customStyles.chartContainer}>
        {isProgressLoading ? (
          <Skeleton variant="rectangular" height={250} />
        ) : progressData?.chartData.length && reportType ? (
          <BarsGroupChart
            dataKeys={progressData.dataKeys}
            data={progressData.chartData}
            xAxisLabel="xAxisLabel"
            clickHandler={clickHandler}
            barCategoryGap={getBarCategoryGap(reportType)}
            cursorValue={
              reportType === ReportType.Class_Report ? "default" : "pointer"
            }
            isPrinting={isPrinting}
          />
        ) : (
          <DataNotFound text={t("No data")} />
        )}
      </Typography>
    </>
  );
}

export default ProgressSection;
